import { getMainDefinition } from "shared/providers/apollo/apollo-utilities.package";
import { Observable } from "shared/providers/apollo/apollo.package";
import { onError } from "shared/providers/apollo/link/error.package";

import { userPropertyDataVar } from "./vars/user-property-data.var";
import { requestClosedPeriodReason } from "./vars/closed-period-override.var";

import checkPermissions from "../../utils/check-permission";
import { PERIOD_CLOSED_CODE } from "../../utils/period-closed";

export const CLOSED_PERIOD_REASON_HEADER = "x-closed-period-reason";

const isMutation = (query: Parameters<typeof getMainDefinition>[0]) => {
  const definition = getMainDefinition(query);

  return (
    definition.kind === "OperationDefinition" &&
    definition.operation === "mutation"
  );
};

/**
 * "Post anyway" for users with the "Post to closed periods" permission.
 *
 * When a mutation comes back PERIOD_CLOSED, ask for a reason and send the
 * same mutation again with it in the x-closed-period-reason header - the
 * server logs who, when, which period and why. Cancelling hands the original
 * error back, so each screen still shows its usual closed-books message.
 */
const closedPeriodLink = onError(
  ({ graphQLErrors, response, operation, forward }) => {
    const periodClosed = graphQLErrors?.find(
      (err) => err.extensions?.code === PERIOD_CLOSED_CODE
    );

    if (
      !periodClosed ||
      !response ||
      !isMutation(operation.query) ||
      // Already an override - a second refusal goes straight to the screen
      operation.getContext().headers?.[CLOSED_PERIOD_REASON_HEADER] ||
      !checkPermissions({
        userPermissions: userPropertyDataVar()?.role?.permissions,
        permissions: [{ key: "postToClosedPeriods" }],
      })
    ) {
      return;
    }

    return new Observable((observer) => {
      let subscription: { unsubscribe: () => void } | null = null;
      let cancelled = false;

      requestClosedPeriodReason(periodClosed.message).then((reason) => {
        if (cancelled) return;

        if (!reason) {
          observer.next(response);
          observer.complete();
          return;
        }

        operation.setContext(({ headers }: { headers?: object }) => ({
          headers: {
            ...headers,
            [CLOSED_PERIOD_REASON_HEADER]: encodeURIComponent(reason),
          },
        }));

        subscription = forward(operation).subscribe(observer);
      });

      return () => {
        cancelled = true;
        subscription?.unsubscribe();
      };
    });
  }
);

export default closedPeriodLink;
